import React, { useState } from 'react';
import { Plus, Grid3x3, Table as TableIcon, Store, CheckCircle2, XCircle, MapPin, Trash2, Edit, X, Map as MapIcon } from 'lucide-react';
import { useAlert } from '../../../../components/AlertProvider';
import { useConfirm } from '../../../../components/ConfirmDialog';
import api from '../../../../services/api';
import { SalesPoint } from './types';

interface SalesPointsTabProps {
  salesPoints: SalesPoint[];
  clientId: number;
  onReload: () => void;
}

export const SalesPointsTab: React.FC<SalesPointsTabProps> = ({ salesPoints, clientId, onReload }) => {
  const { showSuccess, showError } = useAlert();
  const { showConfirm } = useConfirm();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingPoint, setEditingPoint] = useState<SalesPoint | null>(null);
  const [viewingPoint, setViewingPoint] = useState<SalesPoint | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'list'>('table');
  const [showMapPreview, setShowMapPreview] = useState<boolean>(false);
  const [listMapPreviewPointId, setListMapPreviewPointId] = useState<number | null>(null);

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed` : '';
  };
  const [form, setForm] = useState<Partial<SalesPoint>>({
    name: '',
    code: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    phone: '',
    email: '',
    manager_name: '',
    is_active: true,
    notes: ''
  });

  const openModal = (point?: SalesPoint, isEdit: boolean = false) => {
    if (point) {
      if (isEdit) {
        setEditingPoint(point);
        setForm({
          name: point.name,
          code: point.code || '',
          address: point.address || '',
          city: point.city || '',
          postal_code: point.postal_code || '',
          country: point.country || 'France',
          maps_link: point.maps_link || '',
          phone: point.phone || '',
          email: point.email || '',
          manager_name: point.manager_name || '',
          is_active: point.is_active,
          notes: point.notes || ''
        });
      } else {
        setViewingPoint(point);
      }
    } else {
      setEditingPoint(null);
      setViewingPoint(null);
      setForm({
        name: '',
        code: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        phone: '',
        email: '',
        manager_name: '',
        is_active: true,
        notes: ''
      });
    }
    setShowMapPreview(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      showError('Le nom est obligatoire');
      return;
    }

    try {
      const addressComplete = buildAddressComplete(form.address || '', form.postal_code || '', form.city || '');
      const payload = {
        name: form.name,
        code: form.code || null,
        address: form.address || null,
        city: form.city || null,
        postal_code: form.postal_code || null,
        country: form.country || 'France',
        maps_link: buildMapsLink(addressComplete) || null,
        phone: form.phone || null,
        email: form.email || null,
        manager_name: form.manager_name || null,
        is_active: form.is_active ?? true,
        notes: form.notes || null
      };

      if (editingPoint) {
        await api.patch(`/clients/sales-points/${editingPoint.id}`, payload);
        showSuccess('Point de vente mis à jour avec succès');
      } else {
        await api.post(`/clients/${clientId}/sales-points`, payload);
        showSuccess('Point de vente créé avec succès');
      }

      setShowModal(false);
      onReload();
    } catch (e: any) {
      console.error('Save failed:', e);
      showError(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (point: SalesPoint) => {
    const isLastSalesPoint = salesPoints.length === 1;

    const confirmed = await showConfirm({
      title: isLastSalesPoint ? '⚠️ Attention : Dernier point de vente' : 'Supprimer le point de vente',
      message: isLastSalesPoint
        ? `Vous êtes sur le point de supprimer le dernier point de vente "${point.name}" de ce client. Le client n'aura plus aucun point de vente après cette suppression. Êtes-vous sûr de vouloir continuer ?`
        : `Êtes-vous sûr de vouloir supprimer le point de vente "${point.name}" ? Cette action est irréversible.`,
      confirmText: isLastSalesPoint ? 'Supprimer quand même' : 'Supprimer',
      cancelText: 'Annuler',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/clients/sales-points/${point.id}`);
      showSuccess('Point de vente supprimé avec succès');
      onReload();
    } catch (e: any) {
      console.error('Delete failed:', e);
      const errorDetail = e?.response?.data?.detail || '';

      // Check if error is about linked orders
      if (errorDetail.includes('commande(s)')) {
        const forceConfirmed = await showConfirm({
          title: '⚠️ Commandes liées détectées',
          message: `${errorDetail} Les commandes seront détachées de ce point de vente mais ne seront pas supprimées.`,
          confirmText: 'Confirmer la suppression',
          cancelText: 'Annuler',
        });

        if (forceConfirmed) {
          try {
            await api.delete(`/clients/sales-points/${point.id}?force=true`);
            showSuccess('Point de vente supprimé avec succès');
            onReload();
          } catch (e2: any) {
            console.error('Force delete failed:', e2);
            showError(e2?.response?.data?.detail || 'Erreur lors de la suppression');
          }
        }
      } else {
        showError(errorDetail || 'Erreur lors de la suppression');
      }
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        <h2 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">Points de vente</h2>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* <button
            type="button"
            onClick={handleTriggerWorkflow}
            disabled={salesPoints.length > 0 || triggeringWorkflow}
            className={`inline-flex items-center px-4 py-2 rounded-lg border text-sm font-medium transition-colors shadow-sm ${salesPoints.length > 0 || triggeringWorkflow
              ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50 dark:border-slate-700 dark:text-slate-500 dark:bg-slate-800/40'
              : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'}
            `}
            title={salesPoints.length > 0 ? 'Le client possède déjà un point de vente' : 'Déclencher le workflow client'}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Déclencher workflow
          </button> */}
          <button
            onClick={() => openModal()}
            className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Ajouter un point de vente</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
          {salesPoints.length > 0 && (
            <div className="hidden sm:flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                aria-label="Vue cartes"
                title="Vue cartes"
                className={`p-2 rounded flex items-center justify-center ${viewMode === 'card' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <Grid3x3 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                aria-label="Vue tableau"
                title="Vue tableau"
                className={`p-2 rounded flex items-center justify-center ${viewMode === 'table' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <TableIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {salesPoints.length === 0 ? (
        <div className="text-center py-8 sm:py-12 bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Store className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">Aucun point de vente</h3>
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">Ce client n'a pas encore de point de vente associé.</p>
        </div>
      ) : viewMode === 'card' || window.innerWidth < 640 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
          {salesPoints.map((point) => (
            <div
              key={point.id}
              onClick={() => openModal(point, true)}
              className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow duration-200 overflow-hidden group cursor-pointer"
            >
              <div className="p-3 sm:p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      <Store className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 line-clamp-1">{point.name}</h3>
                    </div>
                  </div>
                  {point.is_active ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-900/50">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/50">
                      <XCircle className="w-3 h-3 mr-1" />
                      Inactif
                    </span>
                  )}
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <MapPin className={`w-4 h-4 ${buildAddressComplete(point.address || '', point.postal_code || '', point.city || '') ? 'text-emerald-500' : 'text-red-500'}`} />
                    </div>
                    {buildAddressComplete(point.address || '', point.postal_code || '', point.city || '') ? (
                      <div className="text-slate-600 dark:text-slate-400">
                        <p>{buildAddressComplete(point.address || '', point.postal_code || '', point.city || '')}</p>
                        {point.country && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">{point.country}</p>
                        )}
                        {buildAddressComplete(point.address || '', point.postal_code || '', point.city || '') && (
                          <div className="mt-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setListMapPreviewPointId((curr) => (curr === point.id ? null : point.id));
                                }}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                  (listMapPreviewPointId === point.id
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                }
                              >
                                <MapIcon className="h-3.5 w-3.5" />
                                Carte
                              </button>
                              <a
                                href={buildMapsLink(buildAddressComplete(point.address || '', point.postal_code || '', point.city || ''))}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Ouvrir dans Google Maps
                              </a>
                            </div>

                            {listMapPreviewPointId === point.id && (
                              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                <iframe
                                  title="Google Maps"
                                  src={buildMapsEmbedSrc(buildAddressComplete(point.address || '', point.postal_code || '', point.city || ''))}
                                  className="w-full h-64"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-400 dark:text-slate-500 italic">Non renseigné</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 px-3 sm:px-5 py-2 sm:py-3 border-t border-slate-100 dark:border-slate-700/50 flex justify-between items-center">
                <span className="text-xs text-slate-500 dark:text-slate-400">Créé le {new Date(point.created_at).toLocaleDateString()}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(point);
                    }}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Point de vente</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Adresse</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Horaire recommandé</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Statut</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Créé le</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {salesPoints.map((point) => (
                <tr
                  key={point.id}
                  onClick={() => openModal(point, true)}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                        <Store className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{point.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">
                    {buildAddressComplete(point.address || '', point.postal_code || '', point.city || '') ? (
                      <>
                        <div>{buildAddressComplete(point.address || '', point.postal_code || '', point.city || '')}</div>
                        {point.country && <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{point.country}</div>}
                        {buildAddressComplete(point.address || '', point.postal_code || '', point.city || '') && (
                          <div className="mt-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setListMapPreviewPointId((curr) => (curr === point.id ? null : point.id));
                                }}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                  (listMapPreviewPointId === point.id
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                }
                              >
                                <MapIcon className="h-3.5 w-3.5" />
                                Carte
                              </button>
                              <a
                                href={buildMapsLink(buildAddressComplete(point.address || '', point.postal_code || '', point.city || ''))}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Ouvrir dans Google Maps
                              </a>
                            </div>

                            {listMapPreviewPointId === point.id && (
                              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                <iframe
                                  title="Google Maps"
                                  src={buildMapsEmbedSrc(buildAddressComplete(point.address || '', point.postal_code || '', point.city || ''))}
                                  className="w-full h-64"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="italic text-slate-400 dark:text-slate-500">Non renseigné</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                    {point.recommended_time || <span className="italic text-slate-400 dark:text-slate-500">—</span>}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {point.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-900/50">
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/50">
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                    {new Date(point.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(point);
                        }}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        aria-label="Supprimer"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="sticky top-0 bg-gradient-to-r from-cyan-500 to-blue-600 px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-base sm:text-xl font-bold text-white">
                {viewingPoint ? 'Détails du point de vente' : editingPoint ? 'Modifier le point de vente' : 'Nouveau point de vente'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* VIEW MODE */}
            {viewingPoint && (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Store className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">{viewingPoint.name}</h3>
                    <div className="mt-3">
                      {viewingPoint.is_active ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-900/50">
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/50">
                          <XCircle className="w-4 h-4 mr-1" />
                          Inactif
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <MapPin className={`w-5 h-5 ${buildAddressComplete(viewingPoint.address || '', viewingPoint.postal_code || '', viewingPoint.city || '') ? 'text-emerald-500' : 'text-red-500'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Adresse</p>
                      {buildAddressComplete(viewingPoint.address || '', viewingPoint.postal_code || '', viewingPoint.city || '') ? (
                        <div className="text-slate-900 dark:text-slate-100">
                          <p className="font-medium">{buildAddressComplete(viewingPoint.address || '', viewingPoint.postal_code || '', viewingPoint.city || '')}</p>
                          {viewingPoint.country && (
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 font-medium">{viewingPoint.country}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-slate-400 dark:text-slate-500 italic">Non renseigné</p>
                      )}
                    </div>
                  </div>
                </div>

                {viewingPoint.notes && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Notes</p>
                    <p className="text-slate-900 dark:text-slate-100 whitespace-pre-wrap">{viewingPoint.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Créé le {new Date(viewingPoint.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setViewingPoint(null);
                        setEditingPoint(viewingPoint);
                        setForm({
                          name: viewingPoint.name,
                          code: viewingPoint.code || '',
                          address: viewingPoint.address || '',
                          city: viewingPoint.city || '',
                          postal_code: viewingPoint.postal_code || '',
                          country: viewingPoint.country || 'France',
                          maps_link: viewingPoint.maps_link || '',
                          phone: viewingPoint.phone || '',
                          email: viewingPoint.email || '',
                          manager_name: viewingPoint.manager_name || '',
                          is_active: viewingPoint.is_active,
                          notes: viewingPoint.notes || ''
                        });
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* EDIT MODE */}
            {!viewingPoint && (
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label htmlFor="sales_point_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom *</label>
                  <input
                    id="sales_point_name"
                    type="text"
                    value={form.name || ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="border-2 border-cyan-300 dark:border-cyan-700 p-4 rounded-lg bg-cyan-50 dark:bg-cyan-900/20">
                  <label className="block text-sm font-medium mb-3 text-cyan-700 dark:text-cyan-300">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Adresse
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="sales_point_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                      <input
                        id="sales_point_address"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        value={form.address || ''}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, address: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: 44 Avenue Gambetta"
                      />
                    </div>
                    <div>
                      <label htmlFor="sales_point_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                      <input
                        id="sales_point_postal_code"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        value={form.postal_code || ''}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, postal_code: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: 17100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sales_point_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                      <input
                        id="sales_point_city"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        value={form.city || ''}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, city: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: Saintes"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="sales_point_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                      <input
                        id="sales_point_address_complete"
                        className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                        value={buildAddressComplete(form.address || '', form.postal_code || '', form.city || '')}
                        readOnly
                      />

                      {buildAddressComplete(form.address || '', form.postal_code || '', form.city || '') && (
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setShowMapPreview((v) => !v)}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                              (showMapPreview
                                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                            }
                            title={showMapPreview ? 'Masquer la carte' : 'Afficher la carte'}
                          >
                            <MapIcon className="h-3.5 w-3.5" />
                            Carte
                          </button>

                          {form.maps_link && (
                            <a
                              href={form.maps_link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Ouvrir dans Google Maps
                            </a>
                          )}
                        </div>
                      )}

                      {showMapPreview && buildAddressComplete(form.address || '', form.postal_code || '', form.city || '') && (
                        <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <iframe
                            title="Google Maps"
                            src={buildMapsEmbedSrc(buildAddressComplete(form.address || '', form.postal_code || '', form.city || ''))}
                            className="w-full h-64"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="sales_point_country" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Pays</label>
                  <input
                    id="sales_point_country"
                    type="text"
                    value={form.country || 'France'}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label htmlFor="sales_point_notes" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                  <textarea
                    id="sales_point_notes"
                    value={form.notes || ''}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="create-active"
                    checked={form.is_active ?? true}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded text-cyan-600 focus:ring-cyan-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                  />
                  <label htmlFor="create-active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Point de vente actif
                  </label>
                </div>

                <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    {editingPoint ? 'Enregistrer' : 'Créer'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
